import type { ComponentType } from 'react';
import { Link } from 'react-router-dom';
import type { ActivityEvent } from './activityFeedApi';
import { resolveActivityCtaPath } from '../../utils/activityCtaPaths';
import {
  activityCategoryShortLabel,
  formatActivityAbsoluteTooltip,
  formatActivityCategory,
  formatActivityRelativeTime,
  formatActivityTimestamp,
} from '../../utils/activityFeedFormat';
import {
  ActivityFeedIconCalendar,
  ActivityFeedIconClock,
  ActivityFeedIconEye,
  ActivityFeedIconFile,
  ActivityFeedIconHeart,
  ActivityFeedIconMessage,
  ActivityFeedIconPlay,
  ActivityFeedIconSparkles,
  ActivityFeedIconUserPlus,
} from './activityFeedIcons';

const actorTone: Record<ActivityEvent['actor_type'], string> = {
  user: 'bg-slate-100 text-slate-700 ring-slate-200',
  recruiter: 'bg-emerald-50 text-emerald-800 ring-emerald-100',
  system: 'bg-sky-50 text-sky-800 ring-sky-100',
};

type Visual = {
  Icon: ComponentType<{ className?: string }>;
  well: string;
};

function activityVisual(category: string): Visual {
  switch (category) {
    case 'recruiter_viewed_profile':
      return {
        Icon: ActivityFeedIconEye,
        well: 'bg-emerald-50 text-emerald-700 ring-emerald-100/90 shadow-sm',
      };
    case 'recruiter_profile_dwell':
      return {
        Icon: ActivityFeedIconClock,
        well: 'bg-amber-50 text-amber-700 ring-amber-100/90 shadow-sm',
      };
    case 'recruiter_liked_section':
      return {
        Icon: ActivityFeedIconHeart,
        well: 'bg-rose-50 text-rose-600 ring-rose-100/90 shadow-sm',
      };
    case 'recruiter_prompt_video_watch':
      return {
        Icon: ActivityFeedIconPlay,
        well: 'bg-violet-50 text-violet-700 ring-violet-100/90 shadow-sm',
      };
    case 'recruiter_chat_request':
      return {
        Icon: ActivityFeedIconMessage,
        well: 'bg-sky-50 text-sky-700 ring-sky-100/90 shadow-sm',
      };
    case 'recruiter_meeting_request':
    case 'recruiter_meeting_approved':
    case 'recruiter_meeting_denied':
    case 'recruiter_meeting_rescheduled':
      return {
        Icon: ActivityFeedIconCalendar,
        well: 'bg-indigo-50 text-indigo-700 ring-indigo-100/90 shadow-sm',
      };
    case 'recruiter_message_received':
      return {
        Icon: ActivityFeedIconMessage,
        well: 'bg-sky-50 text-sky-700 ring-sky-100/90 shadow-sm',
      };
    case 'recruiter_access_request':
    case 'recruiter_access_approved':
    case 'recruiter_access_denied':
      return {
        Icon: ActivityFeedIconUserPlus,
        well: 'bg-slate-100 text-slate-700 ring-slate-200/90 shadow-sm',
      };
    case 'recruiter_saved_profile':
      return {
        Icon: ActivityFeedIconSparkles,
        well: 'bg-emerald-50 text-emerald-800 ring-emerald-100/90 shadow-sm',
      };
    case 'recruiter_expanded_section':
      return {
        Icon: ActivityFeedIconFile,
        well: 'bg-cyan-50 text-cyan-800 ring-cyan-100/90 shadow-sm',
      };
    case 'user_prompt_added':
    case 'user_prompt_updated':
    case 'user_prompt_deleted':
    case 'user_reference_added':
    case 'user_reference_confirmed':
      return {
        Icon: ActivityFeedIconFile,
        well: 'bg-teal-50 text-teal-700 ring-teal-100/90 shadow-sm',
      };
    case 'user_password_changed':
    case 'user_email_verified':
      return {
        Icon: ActivityFeedIconSparkles,
        well: 'bg-amber-50 text-amber-700 ring-amber-100/90 shadow-sm',
      };
    case 'user_google_calendar_connected':
    case 'user_google_calendar_disconnected':
      return {
        Icon: ActivityFeedIconCalendar,
        well: 'bg-emerald-50 text-emerald-700 ring-emerald-100/90 shadow-sm',
      };
    case 'user_profile_updated':
    case 'user_skills_updated':
      return {
        Icon: ActivityFeedIconFile,
        well: 'bg-slate-100 text-slate-700 ring-slate-200/90 shadow-sm',
      };
    case 'system_onboarding_complete':
    case 'system_profile_activated':
    case 'system_profile_deactivated':
      return {
        Icon: ActivityFeedIconSparkles,
        well: 'bg-blue-50 text-blue-700 ring-blue-100/90 shadow-sm',
      };
    default:
      return {
        Icon: ActivityFeedIconSparkles,
        well: 'bg-slate-100 text-slate-600 ring-slate-200/80 shadow-sm',
      };
  }
}

export function ActivityFeedRow({
  event,
  dense,
  compact,
  highlighted,
}: {
  event: ActivityEvent;
  dense?: boolean;
  compact?: boolean;
  highlighted?: boolean;
}) {
  const tsFull = formatActivityTimestamp(event.created_at);
  const tsRelative = formatActivityRelativeTime(event.created_at);
  const tsTooltip = formatActivityAbsoluteTooltip(event.created_at);
  const cat = formatActivityCategory(event.category);
  const shortKind = activityCategoryShortLabel(event.category);
  const ctaHref = resolveActivityCtaPath(
    event.category,
    event.cta_path,
    event.cta_label,
    event.metadata
  );
  const hasCta = Boolean(event.cta_label?.trim() && ctaHref);
  const { Icon, well } = activityVisual(event.category);

  const badgeRow = (
    <div className="flex flex-wrap items-center gap-1.5 gap-y-0.5">
      <span
        className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ring-1 ${actorTone[event.actor_type]}`}
      >
        {event.actor_type}
      </span>
      {cat ? (
        <span className="text-[9px] font-medium uppercase tracking-wide text-slate-500">{cat}</span>
      ) : null}
    </div>
  );

  const cta = hasCta ? (
    <Link
      to={event.cta_path.trim()}
      className="inline-flex items-center gap-0.5 font-medium text-emerald-700 transition-colors hover:text-emerald-900"
    >
      {event.cta_label.trim()}
      <span aria-hidden className="text-emerald-600/80">
        →
      </span>
    </Link>
  ) : null;

  if (compact) {
    return (
      <li
        id={`activity-event-${event.id}`}
        className={`transition-colors hover:bg-slate-50/90 ${
          highlighted ? 'bg-emerald-50/80 ring-2 ring-inset ring-emerald-300' : ''
        }`}
      >
        <div className="flex gap-3 px-3 py-2 sm:px-4 sm:py-2.5">
          <div
            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1 ${well}`}
            aria-hidden
          >
            <Icon className="h-[17px] w-[17px]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <p className="text-[13px] font-medium leading-snug tracking-tight text-slate-900">
                {event.message}
              </p>
              {tsRelative ? (
                <time
                  className="shrink-0 pt-0.5 text-[11px] font-medium tabular-nums text-slate-400"
                  dateTime={event.created_at}
                  title={tsTooltip || tsFull || undefined}
                >
                  {tsRelative}
                </time>
              ) : null}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
              <span className="text-slate-400">{shortKind}</span>
              {hasCta ? (
                <>
                  <span className="text-slate-300" aria-hidden>
                    ·
                  </span>
                  {cta}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className={dense ? 'py-2' : 'py-3'}>
      {badgeRow}
      <p className={`mt-1 text-sm leading-snug text-slate-800 ${dense ? 'line-clamp-2' : ''}`}>
        {event.message}
      </p>
      {tsRelative ? (
        <p className="mt-1 text-[11px] text-slate-500" title={tsTooltip || tsFull || undefined}>
          {tsRelative}
        </p>
      ) : null}
      {hasCta ? (
        <Link
          to={ctaHref}
          className="mt-1.5 inline-block text-xs font-semibold text-emerald-700 hover:text-emerald-900 hover:underline"
        >
          {event.cta_label.trim()}
        </Link>
      ) : null}
    </li>
  );
}
